import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, LayoutGrid, List } from "lucide-react";
import { ItemsGrid, Margin } from "@/components/ItemsGrid";
import { ItemThumb } from "@/components/ItemImage";
import { CatalogueButton, BulkImagesButton } from "@/components/ItemsToolbar";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GstBadge } from "@/components/PageHeader";
import { ItemDialog } from "@/components/ItemDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const StockDot = ({ n }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={`h-2 w-2 rounded-full ${n <= 0 ? "bg-red-500" : n < 10 ? "bg-amber-500" : "bg-emerald-500"}`} />
    <span className="num">{n}</span>
  </span>
);

export default function Items() {
  const { user } = useAuth();
  const admin = user.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState("grid");
  const [cat, setCat] = useState("");
  const cats = [...new Set(rows.map((r) => r.category || "Other"))].sort();
  const shown = cat ? rows.filter((r) => (r.category || "Other") === cat) : rows;

  const load = useCallback(() => api.get("/items", { params: { q } }).then((r) => setRows(r.data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const del = async (it) => {
    if (!window.confirm(`Delete ${it.sku}?`)) return;
    try { await api.delete(`/items/${it.id}`); toast.success("Item deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Items" subtitle={`${shown.length} items in inventory`}>
        <CatalogueButton category={cat} />
        {admin && <BulkImagesButton onDone={load} />}
        {admin && <Button className="bg-[#1BA3E0] hover:bg-[#1590C8]" onClick={() => { setEdit(null); setOpen(true); }} data-testid="new-item-btn"><Plus className="mr-1 h-4 w-4" /> New item</Button>}
      </PageHeader>
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="space-y-3 border-b border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input placeholder="Search SKU, name, category" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="items-search-input" />
            </div>
            <div className="ml-auto flex rounded-md border border-slate-200 p-0.5">
              <Button size="sm" variant={view === "grid" ? "default" : "ghost"} className={view === "grid" ? "bg-[#01195D]" : ""} onClick={() => setView("grid")} data-testid="items-view-grid"><LayoutGrid className="h-4 w-4" /></Button>
              <Button size="sm" variant={view === "list" ? "default" : "ghost"} className={view === "list" ? "bg-[#01195D]" : ""} onClick={() => setView("list")} data-testid="items-view-list"><List className="h-4 w-4" /></Button>
            </div>
          </div>
          {cats.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="items-category-chips">
              {["", ...cats].map((c) => (
                <button key={c || "all"} onClick={() => setCat(c)} data-testid={`items-cat-${c || "all"}`}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${cat === c ? "border-[#1BA3E0] bg-[#1BA3E0] text-white" : "border-[#1BA3E0]/40 text-[#1BA3E0] hover:bg-[#1BA3E0]/10"}`}>
                  {c || "All"}
                </button>
              ))}
            </div>
          )}
        </div>
        {view === "grid" ? <ItemsGrid rows={shown} admin={admin} onEdit={(it) => { setEdit(it); setOpen(true); }} onUploaded={load} /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="items-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 text-right">Price ex GST</th>{admin && <><th className="px-4 py-3 text-right">Cost</th><th className="px-4 py-3 text-right">Margin</th></>}<th className="px-4 py-3">GST</th><th className="px-4 py-3">Stock</th>{admin && <th />}</tr>
            </thead>
            <tbody>
              {shown.map((it) => (
                <tr key={it.id} className="row-hover border-t border-slate-100" data-testid={`item-row-${it.sku}`}>
                  <td className="num px-4 py-3 text-xs font-semibold text-[#01195D]"><div className="flex items-center gap-3"><ItemThumb item={it} className="h-10 w-10 rounded" />{it.sku}</div></td>
                  <td className="px-4 py-3"><div className="font-medium text-[#0B1523]">{it.name}</div>{it.description && <div className="text-xs text-slate-500">{it.description}</div>}</td>
                  <td className="px-4 py-3 text-slate-500">{it.category}</td>
                  <td className="num px-4 py-3 text-right">{money(it.unit_price)}{it.unit && <span className="text-xs text-slate-400"> /{it.unit}</span>}</td>
                  {admin && <><td className="num px-4 py-3 text-right text-slate-500" data-testid={`item-cost-${it.sku}`}>{money(it.cost_price)}</td><td className="num px-4 py-3 text-right"><Margin it={it} /></td></>}
                  <td className="px-4 py-3"><GstBadge on={it.has_gst} /></td>
                  <td className="px-4 py-3"><StockDot n={it.stock} /></td>
                  {admin && (
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Button size="icon" variant="ghost" onClick={() => { setEdit(it); setOpen(true); }} data-testid={`item-edit-${it.sku}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(it)} data-testid={`item-delete-${it.sku}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
                    </td>
                  )}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="px-4 py-16 text-center text-slate-500" data-testid="items-empty">No items yet. {admin ? "Upload your inventory from the Admin tab." : ""}</td></tr>}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {admin && <ItemDialog open={open} onOpenChange={setOpen} item={edit} onSaved={load} />}
    </div>
  );
}
